//  Third-party
import { deepClone } from '@/utils/objects';

// Constants and types
import BenchmarkPortfolio from '@/types/benchmark-portfolio';
import ComparisonBenchmark from '@/types/comparison-benchmark';
import ComparisonItem from '@/types/comparison-item';

// Utils
import { benchmarkHoldingsToComparisonHoldings, getBenchmarkCurrency } from '../benchmarks';
import { COMPARISON_ITEM_TYPES } from '@/utils/constants';
import disclaimers from '@/utils/disclaimers';
import { LOCALE } from '@/utils/i18n';
import { PORTFOLIO_TYPES } from '@/utils/portfolios';
import { calculateGrowth } from '@/utils/portfolio/utils';
import { calculatePerformanceCommonInception, initPercentGrowth, initRiskData } from '@/utils/calculation-utils';
import ComparisonItemPerformance from '@/types/comparison-item-performance';
import CommonInception from '@/types/common-inception';

const benchmarkToComparisonItem = (benchmarkPortfolio: BenchmarkPortfolio, data?: ComparisonBenchmark[], commonInception?: CommonInception): ComparisonItem => {
  const holdings = benchmarkHoldingsToComparisonHoldings(benchmarkPortfolio.holdings, data);
  const riskFreeHoldings = benchmarkHoldingsToComparisonHoldings(benchmarkPortfolio.holdings, data, true);
  const holdingsBaseCurrency = deepClone(holdings).map(h => ({ ...h, rolling: h.baseRolling }));

  const rolling = calculateGrowth(holdings);
  const rollingBase = calculateGrowth(holdingsBaseCurrency);

  const risk = (benchmarkPortfolio.performance && benchmarkPortfolio.performance.risk) || initRiskData(true);
  const riskPerformanceBase = (benchmarkPortfolio.performanceBase && benchmarkPortfolio.performanceBase.risk) || initRiskData(true);

  let performance: ComparisonItemPerformance = { ...benchmarkPortfolio.performance };
  performance = {
    ...performance,
    percentGrowth: performance.percentGrowth
      ? {
          ...performance.percentGrowth,
          CI: calculatePerformanceCommonInception(rolling.monthly, commonInception?.startTimestamp),
        }
      : initPercentGrowth(),
    ...risk,
    rolling,
    riskFreeRolling: calculateGrowth(riskFreeHoldings),
  };

  let performanceBase: ComparisonItemPerformance = { ...benchmarkPortfolio.performanceBase };
  performanceBase = {
    ...performanceBase,
    percentGrowth: performanceBase.percentGrowth
      ? {
          ...performanceBase.percentGrowth,
          CI: calculatePerformanceCommonInception(rollingBase.monthly, commonInception?.startTimestamp),
        }
      : initPercentGrowth(),
    ...risk,
    ...riskPerformanceBase,
    rolling: rollingBase,
    riskFreeRolling: calculateGrowth(riskFreeHoldings),
  };

  const newItem: ComparisonItem = {
    _id: benchmarkPortfolio._id,
    name: {
      [LOCALE.EN_CA]: benchmarkPortfolio.name,
      [LOCALE.FR_CA]: benchmarkPortfolio.name,
    },
    holdings,
    portfolioType: benchmarkPortfolio.dates && benchmarkPortfolio.dates.savedAt ? PORTFOLIO_TYPES.CUSTOM : undefined,
    currency: getBenchmarkCurrency(benchmarkPortfolio),
    performance,
    performanceBase,
    type: COMPARISON_ITEM_TYPES.BENCHMARK,
    dates: {
      risk: benchmarkPortfolio.dates.risk,
      portfolioGrowth: benchmarkPortfolio.dates.growth,
    },
  };

  return {
    ...newItem,
    disclaimers: data ? disclaimers.getComparisonItemBenchmarkDisclaimers(newItem, data) : {},
  };
};

export default benchmarkToComparisonItem;
